import { PATHS } from '@/config/paths';
import type { AlertEventRow } from '@/features/admin/types/opsExtended.types';
import type {
  HeaderNotificationCategory,
  HeaderNotificationListItem,
  HeaderNotificationSeverity,
} from '@/features/notifications/types/headerNotification.types';

import { formatHeaderNotificationTime } from './formatHeaderNotificationTime';

/**
 * Suy diễn mức nghiêm trọng từ dòng sự kiện API.
 */
function severityFromEvent(row: AlertEventRow): HeaderNotificationSeverity {
  const s = String(row.severity ?? '').toLowerCase();
  if (s === 'danger' || s === 'critical') return 'danger';
  if (s === 'warning' || s === 'warn') return 'warning';
  return 'info';
}

/**
 * Chuyển sự kiện ngưỡng đang mở thành mục chuông.
 */
export function mapOpenAlertEventsToHeaderItems(
  items: AlertEventRow[],
  t: (key: string, options?: Record<string, string | number>) => string,
  locale: string,
): HeaderNotificationListItem[] {
  return items.map((row) => {
    const id = `alert:${row.id}`;
    const message =
      typeof row.message === 'string' && row.message.trim() !== ''
        ? row.message
        : t('notifications.header.untitledAlert');
    const title = t('notifications.header.apiThresholdTitle', { message });
    const rawDate = row.create_date;
    const occurredAt = typeof rawDate === 'string' && rawDate ? rawDate : new Date().toISOString();
    const stationId =
      typeof row.station_id === 'string' && row.station_id.trim() !== ''
        ? row.station_id
        : undefined;
    const openIot = Boolean(stationId);
    return {
      id,
      source: 'api_threshold_event',
      category: 'iot_threshold' as HeaderNotificationCategory,
      severity: severityFromEvent(row),
      title,
      timeLabel: formatHeaderNotificationTime(occurredAt, locale),
      read: false,
      actionKind: openIot ? 'openIotStationDetail' : 'navigate',
      stationId,
      actionPath: openIot ? PATHS.MAP_IOT : PATHS.OPS_REPORTS_ALERTS,
      occurredAt,
    };
  });
}

/**
 * Nhận diện job thất bại từ payload gateway (linh hoạt theo tên trường phổ biến).
 */
function isJobFailedRecord(job: Record<string, unknown>): boolean {
  const s = String(job.status ?? job.state ?? job.phase ?? '').toLowerCase();
  return s === 'failed' || s === 'error' || s === 'cancelled' || s === 'canceled';
}

function idFromJob(job: Record<string, unknown>): string {
  const id = job.id ?? job.job_id;
  if (id != null && id !== '') return String(id);
  return 'unknown';
}

/**
 * Lọc job NetCDF thất bại thành mục chuông.
 */
export function mapFailedNetcdfJobsToHeaderItems(
  jobs: Record<string, unknown>[],
  t: (key: string, options?: Record<string, string | number>) => string,
  locale: string,
): HeaderNotificationListItem[] {
  if (!Array.isArray(jobs)) return [];
  return jobs.filter(isJobFailedRecord).map((job) => {
    const id = `netcdf:${idFromJob(job)}`;
    const jobId = idFromJob(job);
    const title = t('notifications.header.netcdfJobFailed', { id: jobId });
    const raw =
      (typeof job.updated_at === 'string' && job.updated_at) ||
      (typeof job.finished_at === 'string' && job.finished_at) ||
      (typeof job.create_date === 'string' && job.create_date) ||
      new Date().toISOString();
    return {
      id,
      source: 'api_netcdf_job',
      category: 'pipeline_job' as HeaderNotificationCategory,
      severity: 'warning' as const,
      title,
      timeLabel: formatHeaderNotificationTime(raw, locale),
      read: false,
      actionKind: 'navigate',
      actionPath: PATHS.DATA_UPLOAD,
      occurredAt: raw,
    };
  });
}

/**
 * Lọc job ETL thất bại thành mục chuông.
 */
export function mapFailedEtlJobsToHeaderItems(
  jobs: Record<string, unknown>[],
  t: (key: string, options?: Record<string, string | number>) => string,
  locale: string,
): HeaderNotificationListItem[] {
  if (!Array.isArray(jobs)) return [];
  return jobs.filter(isJobFailedRecord).map((job) => {
    const id = `etl:${idFromJob(job)}`;
    const jobId = idFromJob(job);
    const title = t('notifications.header.etlJobFailed', { id: jobId });
    const raw =
      (typeof job.updated_at === 'string' && job.updated_at) ||
      (typeof job.finished_at === 'string' && job.finished_at) ||
      (typeof job.create_date === 'string' && job.create_date) ||
      new Date().toISOString();
    return {
      id,
      source: 'api_etl_job',
      category: 'pipeline_job' as HeaderNotificationCategory,
      severity: 'warning' as const,
      title,
      timeLabel: formatHeaderNotificationTime(raw, locale),
      read: false,
      actionKind: 'navigate',
      actionPath: PATHS.OPS_JOBS,
      occurredAt: raw,
    };
  });
}
